import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../css/bg.css";
import Logo from "../Components/Assets/logo.svg";
import Paper from '../Components/Assets/paper.png';
import segLogo from "../Components/Assets/seg-logo.png";
import Market from "../Components/market/market";
import Background from '../Components/background/background';
import NextBtnLoader from '../Components/loader/loader';
import axios from 'axios';
import config from '../Context/Config';
import DOBPickerInput from "../Components/dobPicker/dobPicker";

const LoginPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [dob, setDob] = useState("");
  const location = useLocation();
  const [error, setError] = useState("");
  const inputRefs = useRef([]);


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get("token");
    config.token = tokenParam;
    if (!tokenParam) {
      navigate("/context-not-found", { state: { openedViaButton: true } });
    }
    else {
      const verifyToken = async () => {
        try {
          setIsLoading(true);
          const response = await axios.post(
            `${config.API_BASE_URL}/verify_token`, // Replace with your Lambda endpoint
            { token: tokenParam }
          );

          const { is_valid, form_submitted } = response.data;

          if (!is_valid) {
            // Token invalid → stay on page
            if (form_submitted) {
              navigate("/form-submission-success", { state: { openedViaLogin: true } });
            }
            else {
              navigate("/unauth", { state: { openedViaButton: false } });
            }

          }
        } catch (error) {
          navigate("/unauth", { state: { openedViaButton: false } });
          // Optional: navigate to error page
        }
        finally {
          setIsLoading(false);
        }
      };
      verifyToken();
    }

  }, [location, navigate]);

  const handleOTPChange = (value, index) => {
    if (!/^\d?$/.test(value)) return; // allow only 1 digit

    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);

    if (value && index < otp.length - 1) {
      // Move to next input
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOTPKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus(); // move back
    }
  };


  const handleOTPPaste = (e) => {
    const paste = e.clipboardData.getData("text");

    if (!/^[0-9]{6}$/.test(paste)) return; // Must be exactly 6 digits

    const updated = paste.split("");
    setOtp(updated);

    // Move focus to last field
    document.getElementById("otp-5").focus();
  };


  const LogoGroup = ({ logos }) => (
    <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
      {logos.map((logo, index) => (
        <img
          key={index}
          src={logo.src}
          onClick={() => window.open(logo.link, "_blank")}
          alt={logo.alt || `Logo ${index + 1}`}
          loading="eager"
          className="w-24 sm:w-32 md:w-40 h-auto rounded-lg bg-white p-2 hover:shadow-lg cursor-pointer transition-shadow duration-200"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      ))}
    </div>
  );


  const logos = [
    { src: Logo, alt: "Main Logo", link: "https://www.retinaspecialty.com/" },
    { src: segLogo, alt: "Secondary Logo", link: "https://southerneyegroup.com/" },
  ];

  const handleSendCode = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${config.API_BASE_URL}/send_otp`, // Replace with your Lambda endpoint
        { token: config.token }
      );

      if (response.status !== 200) {
        // Token invalid → stay on page
        navigate("/unauth", { state: { openedViaButton: false } });
      } else {
        // Token valid → show OTP input
        setShowOTP(true);
      }
    } catch (error) {
      navigate("/unauth", { state: { openedViaButton: false } });
      // Optional: navigate to error page
    } finally {
      setIsLoading(false);
    }
  }


  const validateDOB = (dob) => {

    if (dob.length !== 10) {
      return "Please enter full date (MM/DD/YYYY).";
    }

    const [month, day, year] = dob.split("/").map(Number);


    if (!month || !day || !year) {
      return "Invalid date format.";
    }

    if (month < 1 || month > 12) {
      return "Invalid month.";
    }

    if (day < 1 || day > 31) {
      return "Invalid day.";
    }

    if (year < 1900 || year > new Date().getFullYear()) {
      return "Invalid year.";
    }

    // ✅ iOS-safe date creation
    const parsedDate = new Date(year, month - 1, day);


    if (
      parsedDate.getFullYear() !== year ||
      parsedDate.getMonth() !== month - 1 ||
      parsedDate.getDate() !== day
    ) {
      return "Invalid calendar date.";
    }

    return false;
  };


  const handleLogin = async () => {
    // Blur active input to close keyboard and allow click
    if (document.activeElement) document.activeElement.blur();

    try {
      const dobError = validateDOB(dob);

      if (dobError) {
        setError(dobError);
        return;
      }
      setIsLoading(true);
      const response = await axios.post(
        `${config.API_BASE_URL}/verify_otp`,
        { token: config.token, otp: otp.join(""), dob }
      );

      if (response.status !== 200) {
        navigate("/unauth", { state: { openedViaButton: false } });
      } else {
        config.accessToken = response.data.auth_token;
        navigate("/process");
      }
    } catch (error) {
      navigate("/unauth", { state: { openedViaButton: false } });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="relative min-h-screen w-full overflow-hidden text-gray-900">
      {isLoading && <NextBtnLoader />}
      <Background />
      <div className="flex flex-col items-center justify-center gap-6 py-6 px-4 md:px-12">

        {/* LEFT SIDE: Form */}
        <div className="flex flex-col items-center text-center p-4 w-full md:w-1/2 z-10 bg-white rounded-2xl">
          <LogoGroup logos={logos} />
          <img
            src={Paper}
            className="w-24 sm:w-32 md:w-48 lg:w-56 h-auto mb-4 mx-auto"
            alt="Form"
          />


          <h1 className="text-2xl md:text-4xl font-extrabold mb-4 text-black">
            Patient Intake Forms
          </h1>

          {!showOTP ? (
            <button
              onClick={handleSendCode}
              disabled={isLoading}
              className={`bg-sky-500 text-white py-3 px-6 rounded-2xl shadow-lg w-full max-w-xs
          hover:bg-sky-600 transition-all duration-300 text-sm flex items-center justify-center gap-2
          ${isLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Connecting...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <i className="fa-regular fa-paper-plane"></i>
                  <span>Send Code</span>
                </div>
              )}
            </button>
          ) : (
            <div className="flex flex-col items-center w-full mt-4">
              {/* OTP Fields */}
              <label className="mb-2 text-sm font-medium text-gray-700">Enter OTP</label>
              <div className="flex justify-center gap-2 mb-4">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="tel"
                    maxLength="1"
                    inputMode="numeric"
                    ref={(el) => (inputRefs.current[index] = el)}
                    value={digit}
                    onChange={(e) => handleOTPChange(e.target.value, index)}
                    onKeyDown={(e) => handleOTPKeyDown(e, index)}
                    onPaste={handleOTPPaste}
                    className="w-10 h-10 text-center text-xl font-semibold border border-sky-400 rounded-2xl focus:ring-2 focus:ring-sky-400 outline-none shadow-sm"
                  />
                ))}
              </div>

              {/* DOB */}
              <div className="w-full max-w-xs mb-4">
                <label className="mb-2 block text-sm font-medium text-gray-700">Enter Date of Birth</label>
                <DOBPickerInput
                  value={dob}
                  onChange={(date) => {
                    const formattedDate = date
                      ? `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
                        .getDate()
                        .toString()
                        .padStart(2, "0")}/${date.getFullYear()}`
                      : "";
                    setDob(formattedDate);
                  }}
                  placeholder="MM/DD/YYYY"
                  disabled={isLoading}
                  maxLength={10}
                  inputMode="numeric"
                  className={`w-full border rounded-2xl px-3 py-2 text-center tracking-wide text-lg
              ${error ? "border-red-400" : "border-sky-400"} focus:ring-2 focus:ring-sky-300 outline-none shadow-sm`}
                />

              </div>

              {/* Login Button */}
              <button
                onClick={handleLogin}
                onTouchStart={handleLogin}   // for iOS
                disabled={isLoading || !!validateDOB(dob)}
                className={`bg-sky-500 text-white py-3 px-6 rounded-2xl shadow-lg w-full max-w-xs
                hover:bg-sky-600 flex items-center justify-center gap-2 text-sm
                ${(isLoading || !!validateDOB(dob)) ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Connecting...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <i className="fa fa-sign-in"></i>
                    <span>Access Form</span>
                  </div>
                )}
              </button>


            </div>
          )}

          <p className="text-sm md:text-base mt-6 text-gray-500">Powered By Clear Healthcare</p>
        </div>

        {/* RIGHT SIDE: Market */}
        <div className="w-full md:w-1/2 flex justify-center mt-6 md:mt-0">
          <Market />
        </div>

      </div>

    </div>

  );
};

export default LoginPage;
